import React, { Component } from "react";
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Field } from 'react-final-form';
import { Button, Grid, Table } from 'semantic-ui-react';
import { MedicamentPrice } from 'ts-types/api.types';
import DatePicker from 'components/final-form/DatePicker';
import { composeValidators, mustBeNumber, mustBePositiveNumber, required } from 'utils/validatorUtils';
import Input from 'components/final-form/Input';
import styled from 'styled-components';
import moment from 'moment';
import { FieldArray } from 'react-final-form-arrays';
import EditActionButton from 'components/EditActionButton';
import SaveActionButton from 'components/SaveActionButton';
import CancelActionButton from 'components/CancelActionButton';
import DeleteActionButton from 'components/DeleteActionButton';

const ScrollableTableContainer = styled.div`
  height: 243px;
  overflow-y: auto;
  width: 100%;
  border: 1px solid rgba(34,36,38,.15);
  box-shadow: none;
  border-radius: .28571429rem;

  & .ui.table {
    border: none;
  }

   & .ui.table thead tr:first-child > th {
      position: sticky !important;
      top: 0;
      z-index: 2;
      background: #fff;
  }

  & .ui.table tbody tr:last-child > td {
      border-bottom: 1px solid rgba(34,36,38,.15)
  }
`;

const addButton = {
  marginBottom: 10,
  marginTop: 10
};

const priceInput = {
  width: "50px"
};

const StyledTableCell = styled(Table.Cell)`
 height: 50px;
`;

const priceDate = {
  width: "85px",
  fontSize: "12px"
};

interface Props extends MessageSourceProps {
  medicamentPrice?: Array<Partial<MedicamentPrice>>,
  getMedicamentPrices: (medicamentPrices: Array<MedicamentPrice>) => void
}

interface State {
  medicamentPrice: Array<Partial<MedicamentPrice>>,
  editPrice: boolean,
  editPriceIndex?: number,
  priceCopy?: Partial<MedicamentPrice>,
  errors: Array<boolean>
}

class MedicamentPriceSegment extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const {medicamentPrice = []} = this.props;

    this.state = {
      medicamentPrice: medicamentPrice,
      editPriceIndex: undefined,
      editPrice: false,
      errors: [false, false, false, false]
    }
  }

  validateFields = (price: Partial<MedicamentPrice>): boolean => {
    let errors: Array<boolean> = [];

    errors[0] = price.fromDate == "";
    errors[1] = !price.doctorsPrice;
    errors[2] = !price.billingPrice;
    errors[3] = !price.unitQty;

    this.setState({
      errors: errors
    });

    return errors.includes(true);
  };

  sortPricesByDate = (fields: any): Array<MedicamentPrice> => {
    return fields.value
        .sort((a: any, b: any) => moment(a.fromDate).unix() - moment(b.fromDate).unix())
        .reverse();
  };

  addPrice = (e: any, fields: any, index: number) => {

    e.preventDefault();

    if (fields.valid) {
      fields.push({
        fromDate: moment().format("YYYY-MM-DD"),
        doctorsPrice: "",
        billingPrice: "",
        unitQty: ""
      });

      this.setState({
        editPrice: true,
        editPriceIndex: index,
        priceCopy: undefined
      })
    }

  };

  savePrice = (fields: any, index: number) => {

    const {getMedicamentPrices} = this.props;

    this.validateFields(fields.value[index]);

    if (fields.valid) {
      this.setState({
        editPrice: false,
        editPriceIndex: undefined
      });

      getMedicamentPrices(this.sortPricesByDate(fields));
    }
  };

  editPrice = (index: number, field: Partial<MedicamentPrice>, fields: any): void => {

    if (this.state.editPrice) {
      fields.remove(this.state.editPriceIndex);
      fields.insert(this.state.editPriceIndex, this.state.priceCopy);

      this.setState({
        editPriceIndex: index,
        priceCopy: field
      });
    } else {
      this.setState({
        editPrice: true,
        editPriceIndex: index,
        priceCopy: field
      })
    }

  };

  cancelEditPrice = (fields: any, index: number) => {

    this.setState({
      editPrice: false,
      editPriceIndex: undefined
    });

    fields.remove(index);
    if (this.state.priceCopy) {
      fields.insert(index, this.state.priceCopy);
    }

  };

  deleteMedicamentPrice = (fields: any, index: number) => {

    const {getMedicamentPrices} = this.props;

    fields.remove(index);

    let filteredFields = {...fields};
    filteredFields.value.splice(index, 1);
    getMedicamentPrices(this.sortPricesByDate(filteredFields));
  };

  render(): React.ReactNode {

    const {getMessage} = this.props;
    const {editPriceIndex, editPrice, errors} = this.state;

    const validator = (value: string) => required(value);

    return (
        <React.Fragment>
          <Grid.Column width={11}>
            {
              <ScrollableTableContainer>
                <Table celled selectable size='small'>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell width={2}>{getMessage('from')}</Table.HeaderCell>
                      <Table.HeaderCell width={2}>{getMessage('doctorsPrice')}</Table.HeaderCell>
                      <Table.HeaderCell width={2}>{getMessage('billingPrice')}</Table.HeaderCell>
                      <Table.HeaderCell width={2}>{getMessage('unitQty')}</Table.HeaderCell>
                      <Table.HeaderCell width={2}>{this.props.getMessage('actions')}</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    <FieldArray name="medicamentPrices">
                      {({fields}) =>
                          fields.map((field, index) => {
                                if (editPrice && editPriceIndex === index) {
                                  return (
                                      <Table.Row key={index}>
                                        <Table.Cell width={3}>
                                          <Field
                                              name={`${field}.fromDate`}
                                              component={DatePicker}
                                              style={priceDate}
                                              validate={validator}
                                              size='mini'
                                              meta={{touched: errors[0], error: errors[0]}}
                                          />
                                        </Table.Cell>
                                        <Table.Cell width={2}>
                                          <Field
                                              name={`${field}.doctorsPrice`}
                                              component={Input}
                                              style={priceInput}
                                              size='mini'
                                              error={errors[1]}
                                              validate={composeValidators(mustBeNumber, mustBePositiveNumber)}
                                          />
                                        </Table.Cell>
                                        <Table.Cell width={2}>
                                          <Field
                                              name={`${field}.billingPrice`}
                                              component={Input}
                                              style={priceInput}
                                              size='mini'
                                              error={errors[2]}
                                              validate={composeValidators(mustBeNumber, mustBePositiveNumber)}
                                          />
                                        </Table.Cell>
                                        <Table.Cell width={2}>
                                          <Field
                                              name={`${field}.unitQty`}
                                              component={Input}
                                              style={priceInput}
                                              validate={composeValidators(mustBeNumber, mustBePositiveNumber)}
                                              size='mini'
                                              error={errors[3]}
                                          />
                                        </Table.Cell>
                                        <Table.Cell width={2}>
                                          <SaveActionButton hoverMessage={getMessage('button.save')}
                                                            onConfirm={() => this.savePrice(fields, index)}/>
                                          <CancelActionButton hoverMessage={getMessage('button.cancel')}
                                                              onConfirm={() => this.cancelEditPrice(fields, index)}/>
                                        </Table.Cell>
                                      </Table.Row>
                                  )

                                } else {
                                  const price = fields.value[index];

                                  if (price !== undefined) {
                                    return (
                                        <Table.Row
                                            key={index}
                                        >
                                          <Table.Cell width={3}>
                                            {moment(price.fromDate).format("DD.MM.YYYY")}
                                          </Table.Cell>
                                          <StyledTableCell width={2}>
                                            {price.doctorsPrice}
                                          </StyledTableCell>
                                          <Table.Cell width={2}>
                                            {price.billingPrice}
                                          </Table.Cell>
                                          <Table.Cell width={2}>
                                            {price.unitQty}
                                          </Table.Cell>
                                          <Table.Cell width={2}>
                                            <EditActionButton
                                                hoverMessage={getMessage('guarantor.edit')}
                                                onConfirm={() => this.editPrice(index, price, fields)}
                                            />
                                            <DeleteActionButton
                                                hoverMessage={getMessage('msgDelete')}
                                                onConfirm={() => this.deleteMedicamentPrice(fields, index)
                                                }
                                            />
                                          </Table.Cell>
                                        </Table.Row>
                                    )
                                  }
                                }
                              }
                          )
                      }
                    </FieldArray>
                  </Table.Body>
                </Table>
              </ScrollableTableContainer>
            }
            <FieldArray name="medicamentPrices">
              {({fields}) => (
                  <Button style={addButton} compact primary onClick={(e) => this.addPrice(e, fields, fields.length!)}>
                    {this.props.getMessage('button.add')}
                  </Button>
              )
              }
            </FieldArray>
          </Grid.Column>
        </React.Fragment>
    );
  }

}

export default withMessages(MedicamentPriceSegment);
